/**
 * Approval Utilities
 * Helper functions for working with approval flows
 */

import {
  UserApprovalDetailsResponse,
  UserApprovalDetails,
  EnrollmentSetting,
  ApprovalActionPayload,
  IndividualApprovalPayload,
  BulkApprovalPayload,
  FieldApprovalDecision,
  FieldApprovalState,
  ApprovalAction,
  FieldApprovalStatus,
} from './approvals.interface';

/**
 * Check if enrollment uses individual (field-level) approval
 */
export function isIndividualApprovalType(approvalType: number): boolean {
  return approvalType === 1;
}

/**
 * Determine overall action based on field approvals
 * Returns REJECT if any field is rejected, otherwise APPROVE
 */
export function determineOverallAction(
  fieldStates: Map<number, FieldApprovalState>
): ApprovalAction {
  for (const state of fieldStates.values()) {
    if (state.approval_status === 2) {
      // 2 = Rejected
      return 2; // REJECT
    }
  }
  return 1; // APPROVE
}

/**
 * Check if all fields have been reviewed (not in pending state)
 */
export function areAllFieldsReviewed(
  fieldStates: Map<number, FieldApprovalState>
): boolean {
  for (const state of fieldStates.values()) {
    if (state.approval_status === 0) {
      // 0 = Pending
      return false;
    }
  }
  return true;
}

/**
 * Get count of approved, rejected, and pending fields
 */
export function getFieldApprovalCounts(fieldStates: Map<number, FieldApprovalState>): {
  approved: number;
  rejected: number;
  pending: number;
  total: number;
} {
  let approved = 0;
  let rejected = 0;
  let pending = 0;

  fieldStates.forEach((state) => {
    if (state.approval_status === 1) approved++;
    else if (state.approval_status === 2) rejected++;
    else pending++;
  });

  return {
    approved,
    rejected,
    pending,
    total: fieldStates.size,
  };
}

/**
 * Build field approvals array from field states
 */
export function buildFieldApprovals(
  fieldStates: Map<number, FieldApprovalState>
): FieldApprovalDecision[] {
  const fieldApprovals: FieldApprovalDecision[] = [];

  fieldStates.forEach((state) => {
    fieldApprovals.push({
      field_master_id: state.field_master_id,
      approval_status: state.approval_status,
      comments: state.comments || undefined,
    });
  });

  return fieldApprovals;
}

/**
 * Create individual approval payload
 */
export function createIndividualApprovalPayload(
  userId: number,
  fieldStates: Map<number, FieldApprovalState>,
  globalComments?: string,
  rejectionReason?: string
): IndividualApprovalPayload {
  const fieldApprovals = buildFieldApprovals(fieldStates);
  const action = determineOverallAction(fieldStates);

  return {
    target_user_id: userId,
    action,
    field_approvals: fieldApprovals,
    comments: globalComments || undefined,
    rejection_reason: action === 2 ? rejectionReason : undefined,
  };
}

/**
 * Create bulk approval payload
 */
export function createBulkApprovalPayload(
  userId: number,
  action: ApprovalAction,
  comments?: string,
  rejectionReason?: string
): BulkApprovalPayload {
  return {
    target_user_id: userId,
    action,
    comments: comments || undefined,
    rejection_reason: action === 2 ? rejectionReason : undefined,
  };
}

/**
 * Validate individual approval before submission
 * Returns { valid: boolean, error?: string }
 */
export function validateIndividualApproval(
  fieldStates: Map<number, FieldApprovalState>,
  rejectionReason?: string
): { valid: boolean; error?: string } {
  // Check if all fields are reviewed
  if (!areAllFieldsReviewed(fieldStates)) {
    return {
      valid: false,
      error: 'Please review all fields before submitting',
    };
  }

  // Check if rejection reason is provided when fields are rejected
  const hasRejections = determineOverallAction(fieldStates) === 2;
  if (hasRejections && !rejectionReason?.trim()) {
    return {
      valid: false,
      error: 'Rejection reason is required when rejecting fields',
    };
  }

  return { valid: true };
}

/**
 * Validate bulk approval before submission
 * Returns { valid: boolean, error?: string }
 */
export function validateBulkApproval(
  action: ApprovalAction,
  rejectionReason?: string
): { valid: boolean; error?: string } {
  // Check if rejection reason is provided when rejecting
  if (action === 2 && !rejectionReason?.trim()) {
    return {
      valid: false,
      error: 'Rejection reason is required when rejecting',
    };
  }

  return { valid: true };
}

/**
 * Initialize field states from approval fields
 */
export function initializeFieldStates(
  response: UserApprovalDetails
): Map<number, FieldApprovalState> {
  const fieldStates = new Map<number, FieldApprovalState>();

  response.approval_fields.forEach((field) => {
    fieldStates.set(field.field_master_id, {
      field_master_id: field.field_master_id,
      field_code: field.field_code,
      field_name: field.field_name,
      field_value: field.field_value,
      approval_status: 0, // 0 = Pending
      comments: '',
      data_type: field.data_type,
    });
  });

  return fieldStates;
}

/**
 * Update field approval status
 */
export function updateFieldStatus(
  fieldStates: Map<number, FieldApprovalState>,
  fieldMasterId: number,
  status: FieldApprovalStatus
): Map<number, FieldApprovalState> {
  const state = fieldStates.get(fieldMasterId);
  if (state) {
    state.approval_status = status;
    fieldStates.set(fieldMasterId, { ...state });
  }
  return new Map(fieldStates);
}

/**
 * Update field comments
 */
export function updateFieldComments(
  fieldStates: Map<number, FieldApprovalState>,
  fieldMasterId: number,
  comments: string
): Map<number, FieldApprovalState> {
  const state = fieldStates.get(fieldMasterId);
  if (state) {
    state.comments = comments;
    fieldStates.set(fieldMasterId, { ...state });
  }
  return new Map(fieldStates);
}

/**
 * Get approval status text
 */
export function getApprovalStatusText(status: FieldApprovalStatus): string {
  switch (status) {
    case 0:
      return 'Pending';
    case 1:
      return 'Approved';
    case 2:
      return 'Rejected';
    default:
      return 'Unknown';
  }
}

/**
 * Get action text
 */
export function getActionText(action: ApprovalAction): string {
  return action === 1 ? 'Approve' : 'Reject';
}

/**
 * Check if user can approve current tier
 */
export function canApproveCurrentTier(
  response: UserApprovalDetails
): boolean {
  return (
    response.approver_permissions.can_approve &&
    response.approver_permissions.can_approve_current_tier
  );
}

/**
 * Check if approval is auto-approved
 */
export function isAutoApproval(response: UserApprovalDetails): boolean {
  return response.approver_permissions.auto_approval === 1;
}

/**
 * Constants
 */
export const APPROVAL_CONSTANTS = {
  ACTIONS: {
    APPROVE: 1 as ApprovalAction,
    REJECT: 2 as ApprovalAction,
  },
  FIELD_STATUS: {
    PENDING: 0 as FieldApprovalStatus,
    APPROVED: 1 as FieldApprovalStatus,
    REJECTED: 2 as FieldApprovalStatus,
  },
  APPROVAL_TYPE: {
    INDIVIDUAL: 1,
  },
} as const;
